import React from 'react';
import Measure, { ContentRect } from 'react-measure';
import Popover from '@mui/material/Popover';
import { Box } from '@mui/material';

import { UserRole } from '../../core/constants';

import {
  NavBarAvatar,
  NavBarUser,
  SideBarButton,
  StyledToolbar,
  UserModalLayout,
  MenuList,
  MenuHeader,
  MenuAvatar,
  HeaderText,
  UserName,
  UserEmail,
  ProfileLink,
  MenuDivider,
  AccountBlock,
  SectionLabel,
  AccountName,
  AccountNumber,
  MenuSection,
  MenuItemButton,
  MenuFooter,
  FooterButton,
  FooterLink,
  TriggerLabel,
  TriggerChevron,
} from './styles';
import { NavBarProps } from './types';
import { SideBarIconExpand, SideBarIconCollapsed } from './Icons/SideBarIcon';
import { SERVICE_USER_TERMS_URL } from './constants';

const NavBar: React.FunctionComponent<NavBarProps> = ({
  collapseSubSideBar,
  isSubSideBarCollapsed,
  onLogout,
  onRedirectToUserDetails,
  onOpenHelpCentre,
  onOpenWhatsNew,
  setNavBarHeight,
  user,
  helpPanel,
}) => {
  const [anchorSettings, setAnchorSettings] = React.useState<null | HTMLElement>(null);

  const showSettings = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorSettings(event.currentTarget);
  };

  const hideSettings = (): void => {
    setAnchorSettings(null);
  };

  const logout = (): void => {
    hideSettings();
    onLogout();
  };

  const openProfile = (): void => {
    hideSettings();
    onRedirectToUserDetails();
  };

  const openHelpCentre = (): void => {
    hideSettings();
    onOpenHelpCentre?.();
  };

  const openWhatsNew = (): void => {
    hideSettings();
    onOpenWhatsNew?.();
  };

  const popover = (
    <Popover
      open={!!anchorSettings}
      anchorEl={anchorSettings}
      onClose={hideSettings}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      <UserModalLayout>
        <MenuList>
          <MenuHeader>
            <MenuAvatar name={user.name} />
            <HeaderText>
              <UserName>{user.name}</UserName>
              <UserEmail>{user.email}</UserEmail>
              {user.role === UserRole.ArcOperator ? null : (
                <ProfileLink type="button" onClick={openProfile} id="navbar-profile-preferences">
                  Profile &amp; preferences
                </ProfileLink>
              )}
            </HeaderText>
          </MenuHeader>

          <MenuDivider />

          <AccountBlock>
            <SectionLabel>Account</SectionLabel>
            {user.accountName ? <AccountName>{user.accountName}</AccountName> : null}
            <AccountNumber>Account number {user.userId}</AccountNumber>
          </AccountBlock>

          <MenuDivider />

          <MenuSection>
            {onOpenHelpCentre ? (
              <MenuItemButton type="button" onClick={openHelpCentre} id="navbar-help-centre">
                Help Centre
              </MenuItemButton>
            ) : null}
            {onOpenWhatsNew ? (
              <MenuItemButton type="button" onClick={openWhatsNew} id="navbar-whats-new">
                What&apos;s new
              </MenuItemButton>
            ) : null}
          </MenuSection>

          <MenuDivider />

          <MenuFooter>
            <FooterButton type="button" onClick={logout} id="navbar-logout">
              Sign out
            </FooterButton>
            <FooterLink
              href={SERVICE_USER_TERMS_URL}
              target="_blank"
              rel="noopener noreferrer"
              id="navbar-service-user-terms"
            >
              Service User Terms
            </FooterLink>
          </MenuFooter>
        </MenuList>
      </UserModalLayout>
    </Popover>
  );

  return (
    <Measure
      bounds
      onResize={(contentRect: ContentRect) => {
        if (contentRect.bounds && setNavBarHeight) {
          setNavBarHeight(contentRect.bounds.height);
        }
      }}
    >
      {() => (
        <StyledToolbar>
          <SideBarButton
            onClick={collapseSubSideBar}
            type="button"
            id="collapse-sidebar-button"
            aria-label={isSubSideBarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-expanded={!isSubSideBarCollapsed}
          >
            {isSubSideBarCollapsed ? <SideBarIconExpand /> : <SideBarIconCollapsed />}
          </SideBarButton>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {helpPanel}
            <NavBarUser onClick={showSettings} id="navbar-user">
              <NavBarAvatar name={user.name} />
              {user.accountName ? <TriggerLabel title={user.accountName}>{user.accountName}</TriggerLabel> : null}
              <TriggerChevron />
            </NavBarUser>
            {popover}
          </Box>
        </StyledToolbar>
      )}
    </Measure>
  );
};

export default NavBar;
